import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Pencil, Timer as TimerIcon } from 'lucide-react';
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card } from "../components/ui/Card";
import { useToast } from "../components/ui/Toast";
import { Spinner } from "../components/ui/Spinner";
import { Switch } from "../components/ui/Switch";
import { useToolsApi } from '../apis/tools-apis';
import { Tool } from '../types';
import { formatHelpText } from '../config/helptext-config';
import { testPageHelpText } from '../config/testtoolpage-helptext-config';
import { FormField } from '../components/FormField';
import { DEFAULT_TOOL_TIMEOUT } from '../config/api-config';
interface TestTimerProps {
  isRunning: boolean;
  toolTimeout?: number | string;
}

const TestTimer: React.FC<TestTimerProps> = ({ isRunning, toolTimeout }) => {
  const [elapsedTime, setElapsedTime] = useState<number | null>(null);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (isRunning) {
      // Start from 0 when starting a new test
      setElapsedTime(0);
      
      intervalId = setInterval(() => {
        setElapsedTime(prev => prev !== null ? prev + 1 : 0);
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isRunning]);

  // Don't render anything if we haven't started timing yet
  if (elapsedTime === null) return null;

  return (
    <div className="flex items-center gap-2 text-sm text-gray-600">
      <TimerIcon className="h-4 w-4" />
      <span>
        {elapsedTime}s / {toolTimeout || 'Default'}s
      </span>
    </div>
  );
};

const TestToolPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const toolsApi = useToolsApi();
  const [tool, setTool] = useState<Tool | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [parameterValues, setParameterValues] = useState<Record<string, string>>({});
  const [isTestingInProgress, setIsTestingInProgress] = useState(false);
  const [testResponse, setTestResponse] = useState<any>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [testMode, setTestMode] = useState(true);
  const [isTestRunning, setIsTestRunning] = useState(false);

  const fetchTool = useCallback(async () => {
    if (!id) return;
    
    try {
      const fetchedTool = await toolsApi.getTool(id);
      
      if (fetchedTool) {
        setTool(fetchedTool);
        // Initialize testMode from tool data
        setTestMode(fetchedTool.toolParameters.testMode);
        const initialValues: Record<string, string> = {};
        fetchedTool.toolParameters.parameters.forEach(param => {
          initialValues[param.key] = '';
        });
        setParameterValues(initialValues);
      } else {
        addToast({
          title: "Error",
          description: "Tool not found",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error fetching tool:', error);
      addToast({
        title: "Error",
        description: "Failed to load tool details",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [id, toolsApi, addToast]);

  useEffect(() => {
    let isMounted = true;

    const fetchTool = async () => {
      if (!id) return;
      
      try {
        const fetchedTool = await toolsApi.getTool(id);
        if (!isMounted) return;

        if (fetchedTool) {
          setTool(fetchedTool);
          // Initialize testMode from tool data if available, otherwise keep default true
          // setTestMode(fetchedTool.toolParameters.testMode ?? true);
          setTestMode(true); // Always start with test mode enabled
          const initialValues: Record<string, string> = {};
          fetchedTool.toolParameters.parameters.forEach(param => {
            initialValues[param.key] = '';
          });
          setParameterValues(initialValues);
        } else {
          addToast({
            title: "Error",
            description: "Tool not found",
            variant: "destructive",
          });
        }
      } catch (error) {
        if (!isMounted) return;
        console.error('Error fetching tool:', error);
        addToast({
          title: "Error",
          description: "Failed to load tool details",
          variant: "destructive",
        });
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchTool();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleTest = async () => {
    if (!tool) return;
  
    setIsTestingInProgress(true);
    setIsTestRunning(true);
    setTestResponse(null);
    setTestError(null);
  
    try {
      const requestBody: Record<string, any> = {};
      const formattedParameters: Record<string, any> = {};
  
      // Process parameters and create formatted parameter object
      tool.toolParameters.parameters.forEach(param => {
        const value = parameterValues[param.key]?.trim();
        if (value) {
          switch (param.type) {
            case 'int':
              formattedParameters[param.key] = parseInt(value);
              break;
            case 'float':
              formattedParameters[param.key] = parseFloat(value);
              break;
            case 'bool':
              formattedParameters[param.key] = value.toLowerCase() === 'true';
              break;
            case 'list':
            case 'dict':
              try {
                formattedParameters[param.key] = JSON.parse(value);
              } catch {
                formattedParameters[param.key] = value;
              }
              break;
            default: // handles 'str' and any other types
              formattedParameters[param.key] = value;
          }
        }
      });
      console.log('Formatted parameters:', formattedParameters);
      console.log('Test request body:', requestBody);
      requestBody['testMode'] = testMode;
      requestBody['toolTimeout'] = tool.toolTimeout || DEFAULT_TOOL_TIMEOUT;
      requestBody['toolUrlEndpoint'] = tool.toolUrlEndpoint;
      requestBody['toolParameters'] = formattedParameters;
      console.log('Test request body:', requestBody);
  
      const response = await toolsApi.testTool(tool.toolId || '', requestBody);
      console.log('Test response:', response);
  
      // Always display the exact response from the API to the user
      setTestResponse(response);
      
      // If no response, set error message
      if (!response) {
        // Set error message based on response if available
        const errorMessage = 'Test failed. No response received.';
        setTestError(errorMessage);
        
        addToast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        // Clear any previous error
        setTestError(null);
        
        addToast({
          title: "Success",
          description: "Test completed successfully",
          variant: "default",
        });
      }
    } catch (error) {
      console.error('Test error:', error);
      setTestError(error instanceof Error ? error.message : 'An unknown error occurred');
      
      addToast({
        title: "Error",
        description: error instanceof Error ? error.message : "Test failed. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsTestingInProgress(false);
      setIsTestRunning(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner className="w-8 h-8" />
      </div>
    );
  }

  if (!tool) {
    return (
      <Card className="max-w-2xl mx-auto m-4">
        <div className="p-6">
          <p className="text-muted-foreground">Tool not found.</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="flex justify-center items-center min-h-screen p-4">
      <Card className="max-w-3xl mx-auto m-4">
        <div className="flex flex-row items-center justify-between p-6 pb-2">
          <h2 className="text-2xl font-semibold">Test Tool</h2>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/studio/tools/edit/${tool.toolId}`, {
                state: { startEditing: true }
              })}
              className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/studio/tools')}
              className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Tools
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Tool Details Section */}
          <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
            <FormField label="Tool Name">
              <p className="text-gray-900">{tool.toolName}</p>
            </FormField>

            <FormField
              label="Tool Timeout"
              helpText={formatHelpText(testPageHelpText.toolTimeout)}
            >
              <p className="text-gray-900">{tool.toolTimeout || DEFAULT_TOOL_TIMEOUT}</p>
            </FormField>

            {tool.toolAnnotation && (
              <FormField
                label="Annotation"
                helpText={formatHelpText(testPageHelpText.annotation)}
              >
                <pre className="text-sm text-gray-900 whitespace-pre-wrap font-mono bg-white p-4 rounded border">
                  {tool.toolAnnotation}
                </pre>
              </FormField>
            )}
          </div>

          {/* Test Mode Toggle Section */}
          <div className="bg-slate-50 p-6 rounded-lg">
            <FormField
              label="Test Mode"
              helpText={formatHelpText(testPageHelpText.testMode)}
            >
              <div className="flex items-center justify-between mt-2">
                <p className="text-gray-500">Enable or disable test mode for this tool</p>
                <div className="flex items-center gap-4">
                  <Switch
                    checked={testMode}
                    onCheckedChange={setTestMode}
                    disabled={isTestingInProgress}
                  />
                  <span className={`text-lg font-medium ${testMode ? 'text-blue-600' : 'text-gray-400'}`}>
                    {testMode ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </FormField>
          </div>

          {/* Parameters Input Section */}
          <FormField
            label="Input Parameters"
            helpText={formatHelpText(testPageHelpText.inputParameters)}
          >
            <div className="space-y-4 mt-2">
              {tool.toolParameters.parameters.map((param) => (
                <div key={param.key} className="space-y-2">
                  <label className="text-sm font-medium">
                    {param.key}
                    <span className="text-gray-500 ml-2">({param.type})</span>
                  </label>
                  {param.description && (
                    <p className="text-sm text-gray-500">{param.description}</p>
                  )}
                  <Input
                    value={parameterValues[param.key]}
                    onChange={(e) => setParameterValues(prev => ({
                      ...prev,
                      [param.key]: e.target.value
                    }))}
                    placeholder={param.example || `Enter ${param.key}`}
                    disabled={isTestingInProgress}
                  />
                  {param.options && (
                    <p className="text-xs text-gray-500">Options: {param.options}</p>
                  )}
                </div>
              ))}
            </div>
          </FormField>

          {/* Results Section */}
          <FormField
            label="Response"
            helpText={formatHelpText(testPageHelpText.results)}
          >
            <div className="bg-slate-50 rounded-lg min-h-8 mt-2">
              <div className={`p-4 transition-all duration-200 ${testResponse || testError ? 'max-h-96 overflow-y-auto' : 'h-12'}`}>
                {isTestingInProgress ? (
                  <div className="flex items-center">
                    <Spinner className="w-4 h-4" />
                    <span className="ml-2 font-mono text-sm">Processing...</span>
                  </div>
                ) : testError ? (
                  <div className="text-red-500 font-mono text-sm">{testError}</div>
                ) : testResponse ? (
                  <pre className="whitespace-pre-wrap font-mono text-sm">
                    {JSON.stringify(testResponse, null, 2)}
                  </pre>
                ) : (
                  <div className="font-mono text-sm text-slate-600">
                    Enter values for Parameters and click Test button to run Tool.
                  </div>
                )}
              </div>
            </div>
          </FormField>

          {/* Test Button */}
          <div className="flex justify-end items-center gap-4 pt-4">
            <TestTimer 
              isRunning={isTestRunning} 
              toolTimeout={tool?.toolTimeout} 
            />
            <Button
              onClick={handleTest}
              disabled={isTestingInProgress}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              {isTestingInProgress ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" />
                  Testing...
                </>
              ) : (
                'Test'
              )}
            </Button>
          </div>

        </div>
      </Card>
    </div>
  );
};

export default TestToolPage;